import React, { useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { CalendarIcon } from '@heroicons/react/24/outline';

export interface CalendarEvent {
  id: string;
  title: string;
  date: string;
}

interface MiniCalendarProps {
  events: CalendarEvent[];
  onSelectDate?: (date: Date, eventsForDay: CalendarEvent[]) => void;
}

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const MiniCalendar: React.FC<MiniCalendarProps> = ({ events, onSelectDate }) => {
  const today = useMemo(() => new Date(), []);
  const [viewDate, setViewDate] = useState<Date>(new Date(today.getFullYear(), today.getMonth(), 1));

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const ev of events) {
      const d = new Date(ev.date);
      if (Number.isNaN(d.getTime())) continue;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      const list = map.get(key) || [];
      list.push(ev);
      map.set(key, list);
    }
    return map;
  }, [events]);

  const monthGrid = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startWeekday = firstDay.getDay(); // 0 = Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const cells: Array<{ date: Date | null }> = [];
    for (let i = 0; i < startWeekday; i++) cells.push({ date: null });
    for (let d = 1; d <= daysInMonth; d++) cells.push({ date: new Date(year, month, d) });
    while (cells.length % 7 !== 0) cells.push({ date: null });
    return cells;
  }, [viewDate]);

  const goPrev = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  const goNext = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  const goToday = () => setViewDate(new Date(today.getFullYear(), today.getMonth(), 1));

  const upcomingThisMonth = useMemo(() => {
    return events
      .map((e) => ({ ...e, _d: new Date(e.date) }))
      .filter((e) =>
        !Number.isNaN(e._d.getTime()) &&
        e._d.getFullYear() === viewDate.getFullYear() &&
        e._d.getMonth() === viewDate.getMonth() &&
        e._d >= new Date(today.getFullYear(), today.getMonth(), today.getDate())
      )
      .sort((a, b) => a._d.getTime() - b._d.getTime())
      .slice(0, 3);
  }, [events, viewDate, today]);

  return (
    <div className="floating-card h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#22d3ee]/20 to-[#38bdf8]/20 flex items-center justify-center mr-3">
            <CalendarIcon className="w-5 h-5 text-[#22d3ee]" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white leading-none">
              {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
            </h2>
            <button
              onClick={goToday}
              className="text-xs text-white/50 hover:text-white/80 transition-colors mt-1"
            >
              Jump to today
            </button>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={goPrev}
            aria-label="Previous month"
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
          >
            <ChevronLeftIcon className="w-4 h-4 text-white/80" />
          </button>
          <button
            onClick={goNext}
            aria-label="Next month"
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
          >
            <ChevronRightIcon className="w-4 h-4 text-white/80" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-white/40 mb-1">
        {WEEKDAYS.map((d, i) => (
          <div key={i}>{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {monthGrid.map((cell, i) => {
          if (!cell.date) {
            return <div key={i} className="h-9" />;
          }
          const key = `${cell.date.getFullYear()}-${cell.date.getMonth()}-${cell.date.getDate()}`;
          const dayEvents = eventsByDay.get(key) || [];
          const isToday = sameDay(cell.date, today);
          const hasEvents = dayEvents.length > 0;
          return (
            <button
              key={i}
              onClick={() => onSelectDate?.(cell.date!, dayEvents)}
              aria-current={isToday ? 'date' : undefined}
              aria-label={`${cell.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}${
                hasEvents ? `, ${dayEvents.length} event${dayEvents.length === 1 ? '' : 's'}` : ''
              }${isToday ? ', today' : ''}`}
              className={`
                relative h-9 rounded-lg text-sm flex items-center justify-center
                transition-all duration-150
                ${isToday
                  ? 'bg-gradient-to-r from-[#22d3ee] to-[#38bdf8] text-white font-semibold shadow-lg shadow-[#22d3ee]/30'
                  : hasEvents
                    ? 'bg-white/5 text-white hover:bg-white/10'
                    : 'text-white/60 hover:bg-white/5'
                }
              `}
            >
              {cell.date.getDate()}
              {hasEvents && !isToday && (
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#22d3ee]" />
              )}
              {hasEvents && isToday && (
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-white" />
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-4 pt-4 border-t border-white/5 flex-1">
        <p className="text-[11px] uppercase tracking-widest text-white/40 font-semibold mb-2">
          Next up
        </p>
        {upcomingThisMonth.length > 0 ? (
          <ul className="space-y-1.5">
            {upcomingThisMonth.map((ev) => (
              <li key={ev.id} className="flex items-start space-x-2 text-sm">
                <span className="text-white/50 tabular-nums w-9 flex-shrink-0">
                  {ev._d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
                <span className="text-white/90 truncate" title={ev.title}>
                  {ev.title}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-white/40">Nothing scheduled this month.</p>
        )}
      </div>
    </div>
  );
};

export default MiniCalendar;
